/**
 * Noise, one-shots and shaping tables — the raw material every synthesised voice is built from.
 *
 * Everything here fills a caller-supplied `Float32Array`. Nothing allocates, nothing touches Web
 * Audio, and nothing is random in the uncontrolled sense: all of it comes through the project's
 * seeded RNG (non-negotiable #7), so two machines that generate the same buffer generate exactly
 * the same samples. That matters more than it sounds like it should — a multiplayer build in
 * which two clients hear *differently shaped* noise would be a desynchronisation you could hear
 * but never debug.
 *
 * ## Why one white buffer and not twelve tailored ones
 *
 * It is tempting to bake a "wind" buffer, a "rumble" buffer and a "rain" buffer. Don't: the
 * spectra all move at runtime (wind gets brighter with speed, rumble darker on grass), so a baked
 * spectrum would have to be re-baked, and re-baking an `AudioBuffer` mid-flight is exactly the
 * allocation the frame path is not allowed to make. One flat white buffer, shaped by filters in
 * the graph, is both cheaper and more expressive.
 *
 * The exception is pink noise, which is here because a 1/f spectrum is what an *outdoor* wind bed
 * actually has, and synthesising it with filters costs more nodes than baking it costs bytes.
 *
 * ## Loop seams
 *
 * A looped buffer clicks if its ends do not meet. White noise is discontinuous everywhere so a
 * seam is inaudible, but pink noise carries real low-frequency content and a hard loop point is a
 * step change you will hear once a cycle, forever. {@link crossfadeLoopSeam} folds the tail back
 * over the head so the loop is continuous. Run it on anything with energy below ~200 Hz.
 *
 * `updateFixed` / `updateFrame`: neither. These run once, at start-up.
 */

import type { Rng } from '../core/rng.js';

/**
 * Fill `out` with white noise in −1…1.
 *
 * Uniform rather than Gaussian: for a signal that is going to be band-limited by a filter and
 * then scaled to a fraction of full-scale, the difference is inaudible, and uniform noise has a
 * bounded peak, which means the master limiter never has to work for it.
 */
export function fillWhiteNoise(out: Float32Array, rng: Rng): void {
  for (let i = 0; i < out.length; i++) out[i] = rng.signed();
}

/**
 * Fill `out` with pink (1/f) noise, normalised to roughly ±1.
 *
 * Paul Kellet's economical filter approximation: seven one-pole sections whose corner
 * frequencies are spread an octave and a bit apart, summed. It tracks a true 1/f slope to within
 * about half a decibel from 10 Hz up, which is far better than this needs, and costs seven
 * multiply-accumulates a sample at bake time and nothing at all afterwards.
 */
export function fillPinkNoise(out: Float32Array, rng: Rng): void {
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  let b3 = 0;
  let b4 = 0;
  let b5 = 0;
  let b6 = 0;
  let peak = 1e-6;
  for (let i = 0; i < out.length; i++) {
    const white = rng.signed();
    b0 = 0.99886 * b0 + white * 0.0555179;
    b1 = 0.99332 * b1 + white * 0.0750759;
    b2 = 0.969 * b2 + white * 0.153852;
    b3 = 0.8665 * b3 + white * 0.3104856;
    b4 = 0.55 * b4 + white * 0.5329522;
    b5 = -0.7616 * b5 - white * 0.016898;
    const value = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
    b6 = white * 0.115926;
    out[i] = value;
    const magnitude = value < 0 ? -value : value;
    if (magnitude > peak) peak = magnitude;
  }
  // Normalise after the fact rather than guessing a scale factor: the filter's gain depends on
  // the sequence, and a buffer that clips is a buffer the limiter spends the whole flight on.
  const scale = 0.95 / peak;
  for (let i = 0; i < out.length; i++) out[i] *= scale;
}

/**
 * Make `out` loop seamlessly by crossfading its last `fadeSamples` over its first.
 *
 * Equal-power (sine/cosine) rather than linear, because two *uncorrelated* noise signals
 * crossfaded linearly dip by 3 dB in the middle — an audible "hole" arriving once per loop,
 * which is precisely the artefact this exists to remove.
 *
 * Shortens the usable buffer: the caller must loop over `out.length − fadeSamples`, which is what
 * {@link seamlessLoopEnd} returns.
 */
export function crossfadeLoopSeam(out: Float32Array, fadeSamples: number): void {
  const fade = Math.min(fadeSamples, Math.floor(out.length / 2));
  if (fade <= 1) return;
  const tail = out.length - fade;
  for (let i = 0; i < fade; i++) {
    const t = i / fade;
    const headGain = Math.sin((t * Math.PI) / 2);
    const tailGain = Math.cos((t * Math.PI) / 2);
    out[i] = out[i] * headGain + out[tail + i] * tailGain;
  }
}

/** The loop end point to use after {@link crossfadeLoopSeam}, in samples. */
export function seamlessLoopEnd(length: number, fadeSamples: number): number {
  return length - Math.min(fadeSamples, Math.floor(length / 2));
}

/**
 * Fill `out` with a wave-shaper curve that turns a sine into a unipolar pulse train.
 *
 * The input is a `WaveShaperNode` curve, so index 0 maps to an input of −1 and the last index to
 * +1. The mapping is `max(0, x)^sharpness`: negative half-cycles vanish, positive ones are pulled
 * into a narrow spike. Feeding an oscillator at the blade-pass frequency through this gives an
 * impulse train locked to the blade passing the fuselage — which is what blade slap physically
 * *is* — with none of the scheduling cost of firing an envelope per blade.
 *
 * Higher `sharpness` is a narrower, harder chop. 6 is a distinct chop; 1 is just a rectified sine.
 */
export function fillPulseCurve(out: Float32Array, sharpness: number): void {
  const n = out.length;
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    out[i] = x <= 0 ? 0 : Math.pow(x, sharpness);
  }
}

/**
 * Fill `out` with a soft-clip curve — a gentle saturator for the engine bus.
 *
 * `tanh`-shaped, so it is linear at small signals and compresses smoothly at large ones. This is
 * not a safety device (the master limiter is), it is a *timbre* device: a piston engine at full
 * power is a distorted sound, and running eight clean sine harmonics through a little saturation
 * is what makes the difference between an aeroplane and an organ.
 */
export function fillSoftClipCurve(out: Float32Array, drive: number): void {
  const n = out.length;
  const k = Math.max(0.001, drive);
  const norm = Math.tanh(k);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    out[i] = Math.tanh(k * x) / norm;
  }
}

/**
 * Fill `out` with one decaying impulse — the body of a thump, a click or a report.
 *
 * `frequencyHz` is the pitch of the body, `decaySeconds` how fast it dies, and `noiseMix` how
 * much of it is a filtered noise burst rather than a tone. A touchdown thump is a low tone with a
 * little noise; a switch click is almost all noise; a cannon report is a low tone with a lot.
 *
 * Baked rather than synthesised live because a one-shot built from live nodes needs an oscillator,
 * a gain, an envelope and a teardown per firing, and the gun fires ten times a second.
 */
export function fillImpulse(
  out: Float32Array,
  sampleRate: number,
  frequencyHz: number,
  decaySeconds: number,
  noiseMix: number,
  rng: Rng,
): void {
  const decay = Math.max(1e-4, decaySeconds);
  const omega = (2 * Math.PI * frequencyHz) / sampleRate;
  const mix = Math.max(0, Math.min(1, noiseMix));
  // A one-pole lowpass on the noise half, so the burst has a body rather than being pure hiss.
  const alpha = Math.min(1, (2 * Math.PI * frequencyHz * 4) / sampleRate);
  let filtered = 0;
  for (let i = 0; i < out.length; i++) {
    const t = i / sampleRate;
    const envelope = Math.exp(-t / decay);
    filtered += alpha * (rng.signed() - filtered);
    const tone = Math.sin(omega * i);
    out[i] = envelope * (tone * (1 - mix) + filtered * mix);
  }
}

/**
 * Fill `out` with one small bird's call.
 *
 * A songbird's call is a whistle whose pitch sweeps, repeated a few times with gaps. That is
 * genuinely most of it — the reason synthesised birds usually sound wrong is that they are given
 * a steady pitch and a flat rhythm, not that they lack a sample. So: two to four syllables, each
 * a 60–160 ms sine sweeping between two frequencies in the 2–5 kHz range, each with a raised-sine
 * envelope so there is no edge at either end, and randomised gaps between them.
 *
 * Everything random comes from the seeded RNG, so the dawn chorus at KSFO is the same dawn chorus
 * on every machine — which is what makes it debuggable, and one day what makes it multiplayer.
 */
export function fillBirdCall(out: Float32Array, sampleRate: number, rng: Rng): void {
  out.fill(0);
  const syllables = rng.int(2, 4);
  let cursor = Math.floor(rng.range(0.02, 0.08) * sampleRate);
  for (let s = 0; s < syllables; s++) {
    const lengthSamples = Math.floor(rng.range(0.06, 0.16) * sampleRate);
    if (cursor + lengthSamples >= out.length) break;
    const startHz = rng.range(2_200, 4_600);
    // Most calls sweep up; a minority fall. Both exist, and having only one is what makes a
    // synthesised bird sound mechanical.
    const endHz = startHz * (rng.bool(0.7) ? rng.range(1.05, 1.6) : rng.range(0.6, 0.95));
    const amplitude = rng.range(0.45, 1);
    let phase = 0;
    for (let i = 0; i < lengthSamples; i++) {
      const t = i / lengthSamples;
      const hz = startHz + (endHz - startHz) * t;
      phase += (2 * Math.PI * hz) / sampleRate;
      // Raised sine: zero slope at both ends, so no click however short the syllable is.
      const envelope = 0.5 - 0.5 * Math.cos(2 * Math.PI * t);
      out[cursor + i] += Math.sin(phase) * envelope * amplitude;
    }
    cursor += lengthSamples + Math.floor(rng.range(0.03, 0.14) * sampleRate);
  }
}
